import { IS_DEVELOPMENT } from 'src/config';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';

export type OfferExpirationDetails = {
  /** The date to show the user as the offer's last day. */
  offerEndDate: Date;
  /** Whole days left of the campaign, rounded up. Only meaningful while the offer is active. */
  daysUntilOfferEnds: number;
  offerHasEnded: boolean;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// The connect payment-interval campaign: switching from monthly to annual or
// biennial billing gets you a discount until this window closes.
const campaignStart = new Date('2026-08-26T12:00:00+02:00');
const campaignEnd = new Date('2026-09-26T23:59:59+02:00');
const previousCampaignEnd = new Date('2026-06-23T23:59:59.999Z');

// Allow Customer Care to offer biennial billing via direct link outside campaigns.
// This does not enable promotional buttons or banners.
export const allowBiennialDirectLink: boolean = true;

function expiredOffer(offerEndDate: Date): OfferExpirationDetails {
  return { offerEndDate, daysUntilOfferEnds: -1, offerHasEnded: true };
}

/**
 * Returns expiration details for the connect payment-interval campaign.
 * `offerActiveOverride` lets callers bypass the real campaign dates: `true`
 * forces the offer active, `false` forces it inactive, and `undefined` (the
 * default) follows the real campaign dates.
 */
export function getOfferExpirationDetails(
  now = new Date(),
  offerActiveOverride?: boolean
): OfferExpirationDetails {
  if (offerActiveOverride === true) {
    return {
      offerEndDate: campaignEnd,
      daysUntilOfferEnds: Math.max(
        1,
        Math.ceil((campaignEnd.getTime() - now.getTime()) / MS_PER_DAY)
      ),
      offerHasEnded: false,
    };
  }

  if (offerActiveOverride === false) {
    return expiredOffer(campaignEnd);
  }

  // The new campaign hasn't started yet.
  if (now < campaignStart) {
    return expiredOffer(previousCampaignEnd);
  }

  // We're past the current campaign period.
  if (now > campaignEnd) {
    return expiredOffer(campaignEnd);
  }

  const daysUntilOfferEnds = Math.ceil(
    (campaignEnd.getTime() - now.getTime()) / MS_PER_DAY
  );

  return {
    offerEndDate: campaignEnd,
    daysUntilOfferEnds,
    offerHasEnded: false,
  };
}

/**
 * Hook for app code to get the current connect campaign's offer expiration
 * details. Reads the `EnableConnectCampaign` feature flag so the offer can
 * be previewed as active or inactive on demand in development, regardless
 * of the real campaign dates.
 */
export function useOfferExpirationDetails(): OfferExpirationDetails {
  const { EnableConnectCampaign } = useFeatureFlags();
  return getOfferExpirationDetails(
    new Date(),
    IS_DEVELOPMENT ? EnableConnectCampaign : undefined
  );
}
